import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { fetchQuotationByToken } from './quotationQueries';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Quotation } from '@/types/schemas';

export function PublicQuotationPage() {
  const { token = '' } = useParams();
  const [q, setQ] = useState<Quotation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public route uses anonymous auth so Firestore rules can let it through if matched on token.
        if (!auth.currentUser) {
          try {
            await signInAnonymously(auth);
          } catch {
            // ignore — security rules also allow unauth read in some emulator setups
          }
        }
        const doc = await fetchQuotationByToken(token);
        if (cancelled) return;
        if (!doc) setError('Quotation not found or link expired.');
        setQ(doc);
      } catch (err) {
        if (!cancelled) setError(String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading)
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;

  if (error || !q)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-6 text-center">
        <img src="/chhabighar.png" alt="" className="mb-4 h-16 w-16" />
        <h1 className="font-serif text-2xl">Quotation unavailable</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-cream px-4 py-6 sm:py-10">
      <div className="mx-auto max-w-2xl rounded-xl border bg-white p-4 shadow-sm sm:p-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-serif text-3xl">CHHABIGHAR</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">
              Quotation
            </div>
          </div>
          <img src="/chhabighar.png" alt="" className="h-14 w-14 rounded" />
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 text-sm">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Prepared for
            </div>
            <div className="truncate font-medium">
              {q.customerSnapshot?.name ?? 'Valued customer'}
            </div>
            {q.customerSnapshot?.phone && (
              <div className="text-xs">{q.customerSnapshot.phone}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Quotation
            </div>
            <div className="font-medium">
              {q.quotationNumber ?? q.id.slice(0, 8).toUpperCase()}
            </div>
            <div className="text-xs">
              {formatDate(
                // @ts-expect-error firestore timestamp
                typeof q.createdAt?.toMillis === 'function'
                  ? // @ts-expect-error firestore timestamp
                    q.createdAt.toMillis()
                  : q.createdAt,
              )}
            </div>
          </div>
        </div>

        <table className="mt-6 w-full text-sm">
          <thead className="border-b text-xs text-muted-foreground">
            <tr>
              <th className="pb-2 text-left">Item</th>
              <th className="pb-2 text-right">Qty</th>
              <th className="pb-2 text-center">Per</th>
              <th className="hidden pb-2 text-right sm:table-cell">Price</th>
              <th className="pb-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {q.items.map((it, i) => (
              <tr key={i} className="border-b">
                <td className="py-2 pr-2">{it.itemName}</td>
                <td className="py-2 text-right tabular-nums">{it.quantity}</td>
                <td className="py-2 text-center text-muted-foreground">{it.per || 'Piece'}</td>
                <td className="hidden py-2 text-right tabular-nums sm:table-cell">
                  {formatCurrency(it.price)}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {formatCurrency(it.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="ml-auto mt-4 w-full max-w-xs space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="tabular-nums">{formatCurrency(q.subtotal)}</span>
          </div>
          {q.deliveryCharge > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delivery Charge</span>
              <span className="tabular-nums">{formatCurrency(q.deliveryCharge)}</span>
            </div>
          )}
          {q.discount > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Discount</span>
              <span className="tabular-nums">-{formatCurrency(q.discount)}</span>
            </div>
          )}
          <div className="flex justify-between border-t pt-2 font-serif text-lg">
            <span>Total</span>
            <span className="tabular-nums">{formatCurrency(q.totalAmount)}</span>
          </div>
        </div>

        {q.notes && (
          <div className="mt-6 rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
            <div className="mb-1 font-medium text-foreground">Notes</div>
            {q.notes}
          </div>
        )}

        <div className="mt-8 text-center text-xs text-muted-foreground">
          ছবি ঘর · CHHABIGHAR
        </div>
      </div>
    </div>
  );
}
